import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Select, Spin, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilter';
import MetricTooltip from '../../../components/dashboard/MetricTooltip';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import {
    DASHBOARD_PACIFIC_TZ,
    formatHubDisplayLabel,
    resolveDashboardComparisonAvgLabel,
} from '../../../utils/dashboardPacificTime';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import { dashboardTableExportCellString } from '../../../utils/dashboardTableExportCellString';
import { showError } from '../../../utils/exceptionUtils';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import { QUERY_QUALITY_DASHBOARD } from '../../../graphql/queries/dashboard/queryQualityDashboard';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import {
    NoUpdateDashboardAggregateItem,
    NoUpdateDashboardItem,
    NoUpdateDashboardQueryInput,
    NoUpdateDashboardResult,
    NoUpdateDashboardDrillTypeInput,
    NoUpdateStatusInput,
    QueryNoUpdateDashboardResponse,
} from '../../../graphql/types/dashboard/NoUpdateDashboardTypes';
import NoUpdateDetailDrawer from './components/NoUpdateDetailDrawer';
import MetricAnnotationCell from '../../../components/dashboardAnnotation/MetricAnnotationCell';
import { useDashboardAnnotations } from '../../../components/dashboardAnnotation/useDashboardAnnotations';
import { buildAnnotationKey } from '../../../components/dashboardAnnotation/buildAnnotationKey';
import {
    dashboardAnnotationRegistry,
    PAGE_KEY_NO_UPDATE,
    type CellContext,
    type DashboardAnnotationColumnType,
} from '../../../components/dashboardAnnotation/registry';
import DashboardMarkLegend from '../../../components/dashboard/DashboardMarkLegend';
import { useDashboardMarkRules } from '../../../hooks/useDashboardMarkRules';
import { DASHBOARD_MARK_CONFIG_KEY } from '../../../graphql/types/dashboard/DashboardMarkRuleTypes';
import {
    appendTargetValueDesc,
    CellMark,
    markCellClassName,
    NO_CELL_MARK,
    resolveCellMark,
} from '../../../utils/dashboardMarkRule';

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

/** 6 个率指标（L1 + 3 个 L2 + 2 个静态 L3；尾程 / 干线运输 L2 下另有动态 breakdown L3） */
type MetricKey =
    | 'totalNoUpdateRate'
    | 'inHubNoUpdateRate'
    | 'linehaulInboundRate'
    | 'inHubOperationRate'
    | 'inHubLinehaulRate'
    | 'lastMileRate';

interface MetricNode {
    key: MetricKey;
    label: string;
    /** 0=L1 hub 行 / 1=L2 类目 / 2=L3 指标 / 3=L4 责任 hub 拆分（仅干线接收/运输按责任 hub 拆分时使用） */
    depth: 0 | 1 | 2 | 3;
    /** 对应明细 drillType（所有指标都支持下钻） */
    drillType: NoUpdateDashboardDrillTypeInput;
    /** 子节点 */
    children?: MetricNode[];
    /** 「不计入汇总」标注（仅干线运输） */
    annotation?: string;
    /** 指标公式提示文本，hover 显示 */
    tooltip?: string;
    /**
     * tooltip 目标值取哪条标记规则，缺省用 key 自身。
     * 下钻子指标后端未单独配规则，指向父级取值。
     */
    targetFieldName?: MetricKey;
}

/** 每个率指标对应的分子件数子字段。用于判断单元格是否可下钻：sum(nums) > 0 才允许。L1/L2 用数仓去重分子列，per-type 用各自件数列 */
const NUM_FIELDS_BY_KEY: Record<MetricKey, (keyof NoUpdateDashboardItem)[]> = {
    totalNoUpdateRate: ['totalNoUpdateCount'],
    inHubNoUpdateRate: ['inHubNoUpdateCount'],
    linehaulInboundRate: ['linehaulInboundCount'],
    inHubOperationRate: ['inHubOperationCount'],
    inHubLinehaulRate: ['inHubLinehaulCount'],
    lastMileRate: ['lastMileCount'],
};

/**
 * 每个率指标对应的分母包裹数子字段；用于「环比标红」规则的「两期分母均 > 1000」判定。
 * totalNoUpdateRate / inHubNoUpdateRate 的分子分母取数仓去重列（totalParcelCount / inHubParcelCount），不再跨 type sum。
 */
const DENOM_FIELDS_BY_KEY: Record<MetricKey, (keyof NoUpdateDashboardItem)[]> = {
    totalNoUpdateRate: ['totalParcelCount'],
    inHubNoUpdateRate: ['inHubParcelCount'],
    linehaulInboundRate: ['linehaulInboundParcelCount'],
    inHubOperationRate: ['inHubOperationParcelCount'],
    inHubLinehaulRate: ['inHubLinehaulParcelCount'],
    lastMileRate: ['lastMileParcelCount'],
};

function sumNumFields(
    it: NoUpdateDashboardItem | NoUpdateDashboardAggregateItem | undefined,
    fields: (keyof NoUpdateDashboardItem)[]
): number {
    if (!it) return 0;
    return fields.reduce(
        (acc, f) => acc + Number(((it as unknown as Record<string, number | undefined>)[f as string]) ?? 0),
        0
    );
}

/** 标红规则：仅作用于「环比」列；周同比 ≥15pp 且两期分母均 > 1000 */
function shouldHighlightMom(
    currRate: string | null | undefined,
    prevRate: string | null | undefined,
    currDenom: number,
    prevDenom: number
): boolean {
    if (currDenom <= 1000 || prevDenom <= 1000) return false;
    const c = parseRatePct(currRate);
    const p = parseRatePct(prevRate);
    if (c == null || p == null) return false;
    return c - p >= 15;
}

/** 看板 hub 行的展示顺序（白名单），未命中的 hub 排在尾部按总包裹量 DESC。值用 formatHubDisplayLabel 后的标签匹配。 */
const HUB_DISPLAY_ORDER = ['LAX', 'EWR', 'MIA', 'ATL', 'ORD', 'DFW', 'HOU', 'LAXS', 'EWRS', 'LAXSCBT'];

/** 这些子 hub 不展示「尾程断更率」L2 整行（含其下动态拆分的 WHS 子行）。值用 formatHubDisplayLabel 后的标签匹配。 */
const HUBS_HIDE_LAST_MILE = new Set(['LAXS', 'EWRS', 'LAXSCBT']);

/** 完全不在看板上展示的 hub（顶层 hub 行 + 干线接收/运输/尾程下钻拆分行均隐藏）。值用 formatHubDisplayLabel 后的标签匹配。 */
const EXCLUDED_HUB_LABELS = new Set(['LAXSTEMU']);

/** 按 HUB_DISPLAY_ORDER 给 displayLabel 排序，未命中的排在尾部并按字母序。 */
function compareHubDisplayLabel(a: string, b: string): number {
    const ia = HUB_DISPLAY_ORDER.indexOf(a);
    const ib = HUB_DISPLAY_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
}

const METRIC_TREE: MetricNode[] = [
    {
        key: 'inHubNoUpdateRate',
        label: '仓内断更率',
        depth: 1,
        drillType: NoUpdateDashboardDrillTypeInput.IN_HUB,
        tooltip: '分子：干线接收和仓内作业的断更包裹数（去重）\n分母：干线接收和仓内作业的包裹数（去重）',
        children: [
            {
                key: 'linehaulInboundRate',
                label: '干线接收断更率',
                depth: 2,
                drillType: NoUpdateDashboardDrillTypeInput.LINEHAUL_INBOUND,
                tooltip: '发生干线运输的包裹中，在到达目的地仓之前有过超96小时断更的包裹比例',
                targetFieldName: 'inHubNoUpdateRate',
            },
            {
                key: 'inHubOperationRate',
                label: '仓内作业断更率',
                depth: 2,
                drillType: NoUpdateDashboardDrillTypeInput.IN_HUB_OPERATION,
                tooltip: '仓内揽收或调拨入库的包裹中，在出库前有过超96小时断更的包裹比例',
                targetFieldName: 'inHubNoUpdateRate',
            },
        ],
    },
    {
        key: 'lastMileRate',
        label: '尾程断更率',
        depth: 1,
        drillType: NoUpdateDashboardDrillTypeInput.LAST_MILE,
        tooltip: '已支线发车或到达目的地whs的包裹中，有过超72小时断更的包裹比例',
        // L3 children 由 tableData 在运行时按 lastMileByResponsibleWhs 动态生成
    },
    {
        key: 'inHubLinehaulRate',
        label: '干线运输断更率',
        depth: 1,
        drillType: NoUpdateDashboardDrillTypeInput.IN_HUB_LINEHAUL,
        annotation: '(不计入汇总)',
        tooltip: '干线运输断更包裹数 / 本仓运输包裹数',
        // L3 children 由 tableData 在运行时按 inHubLinehaulByResponsibleHub 动态生成
    },
];

/** L1 总率（在 hub 顶级行使用） */
const L1_METRIC: MetricNode = {
    key: 'totalNoUpdateRate',
    label: '断更率',
    depth: 0,
    drillType: NoUpdateDashboardDrillTypeInput.TOTAL,
    tooltip: '分子：干线接收、仓内作业和尾程的断更包裹数（去重）\n分母：干线接收、仓内作业和尾程的包裹数（去重）',
};

interface TableRow {
    /** 唯一 key：hubId-depth-metricKey 或 hubId-root */
    key: string;
    hubId: number;
    hubLabel: string;
    /** depth 0 显示 hub 名，depth 1/2/3 显示指标名 */
    label: string;
    metricKey: MetricKey;
    depth: 0 | 1 | 2 | 3;
    drillType?: NoUpdateDashboardDrillTypeInput;
    annotation?: string;
    /** 指标公式提示文本 */
    tooltip?: string;
    /** tooltip 目标值取哪条标记规则，缺省用 metricKey 自身 */
    targetFieldName?: MetricKey;
    /** 责任 hub id（动态 L3「干线运输 → 责任 hub」行才有） */
    responsibleHubId?: number;
    /** 责任 whs id（动态 L3「尾程 → 责任 whs」行才有） */
    responsibleWarehouseId?: number;
    /** 每个时间周期列 → 率字符串 */
    rates: Record<string, string | null | undefined>;
    /** 每个时间周期列 → 分子件数（用于下钻判断） */
    nums: Record<string, number>;
    /** 每个时间周期列 → 分母包裹数（用于环比标红） */
    denoms: Record<string, number>;
    /** avgLabel 列的率 */
    avgRate: string | null | undefined;
    /** avgLabel 列对应分母（用于单期场景的环比标红） */
    avgDenom: number;
    /** 环比文字 */
    momChange: string | null;
    /** 环比是否标红（仅作用在环比列） */
    momHighlight: boolean;
    children?: TableRow[];
}

function parseRatePct(rate: string | null | undefined): number | null {
    if (!rate || rate === '-') return null;
    const n = parseFloat(String(rate).replace('%', ''));
    return Number.isNaN(n) ? null : n;
}

/** 把率字符串格式化为 2 位小数（"4.1277777778%" → "4.13%"） */
function formatRatePct(rate: string | null | undefined): string {
    const n = parseRatePct(rate);
    if (n == null) return '-';
    return `${n.toFixed(2)}%`;
}

/** 明细下钻只支持最近 30 天：检查 period 的起始日是否在今天（美西）前 30 天内 */
function isPeriodWithin30Days(period: string, queryType: ScaleQueryType): boolean {
    const { start } = periodToDateRange(period, queryType);
    if (!start) return false;
    const today = dayjs().tz(DASHBOARD_PACIFIC_TZ).startOf('day');
    const cutoff = today.subtract(30, 'day');
    const periodStart = dayjs.tz(start, DASHBOARD_PACIFIC_TZ);
    return !periodStart.isBefore(cutoff);
}

/**
 * 断更看板：默认不展示近 3 天（美西），与 main 分支 QualitySharedPage 行为一致。
 * 仅对 DAILY 视图生效；T / T-1 / T-2 不展示，从 T-3 起展示。WEEKLY / MONTHLY 不过滤。
 */
function shouldHidePeriodForNoUpdate(period: string, queryType: ScaleQueryType): boolean {
    if (queryType !== ScaleQueryType.DAILY) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return false;
    const todayPacific = dayjs().tz(DASHBOARD_PACIFIC_TZ).startOf('day');
    const cutoffExclusive = todayPacific.subtract(3, 'day').format('YYYY-MM-DD');
    return period > cutoffExclusive;
}

/**
 * 看板指标注释 dateKey：DAILY/WEEKLY 期本身已是具体日期/日期范围，MONTHLY 裸月份
 * （'YYYY-MM'）经 periodToDateRange 展开为当月起止日期，对齐 plan 约定的 RAW dateKey 格式。
 */
function computeNoUpdateAnnotationDateKey(period: string, queryType: ScaleQueryType): string {
    const { start, end } = periodToDateRange(period, queryType);
    if (!start || !end) return period;
    return start === end ? start : `${start}~${end}`;
}

const noUpdateAnnotationConfig = dashboardAnnotationRegistry[PAGE_KEY_NO_UPDATE];

/**
 * 指标行 → 标记规则 fieldName 的归组。
 *
 * 「干线接收 / 仓内作业」是「仓内断更率」的展开下钻，它们的 metricKey 与父行不同，
 * 但共用父行那条规则（需求：行范围写的是「仓内断更率（及展开下钻）」）。
 * 需求里的「干线超96小时」指干线接收，不是「干线运输断更率」（inHubLinehaulRate），后者不参与标记。
 * 未列出的指标（L1 总率、干线运输）不参与标记。
 */
const NO_UPDATE_MARK_RULE_FIELD_BY_METRIC: Partial<Record<MetricKey, string>> = {
    inHubNoUpdateRate: 'inHubNoUpdateRate',
    linehaulInboundRate: 'inHubNoUpdateRate',
    inHubOperationRate: 'inHubNoUpdateRate',
    lastMileRate: 'lastMileRate',
};

/** 图例中的指标简称 */
const NO_UPDATE_MARK_METRIC_LABELS: Record<string, string> = {
    inHubNoUpdateRate: '仓内断更',
    lastMileRate: '尾程断更',
};

const NoUpdateDashboardPage: React.FC = () => {
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    const [selectedHubId, setSelectedHubId] = useState<number>(0);
    const noUpdateStatus = NoUpdateStatusInput.ALL;
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerDrill, setDrawerDrill] = useState<{
        drillType: NoUpdateDashboardDrillTypeInput;
        hubId: number;
        startDate: string;
        endDate: string;
        metricLabel: string;
        responsibleHubId?: number;
        responsibleWarehouseId?: number;
    } | null>(null);

    const { hubOptions, loading: hubsLoading } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        showAllOption: true,
        filterBusinessTypes: [2],
    });

    const queryInput: NoUpdateDashboardQueryInput | null = useMemo(() => {
        if (!timeRange) return null;
        return {
            queryType: timeRange.queryType as unknown as NoUpdateDashboardQueryInput['queryType'],
            startDate: timeRange.startDate,
            endDate: timeRange.endDate,
            hubId: selectedHubId,
            noUpdateStatus,
        };
    }, [timeRange, selectedHubId, noUpdateStatus]);

    const { data, loading, error } = useQuery<QueryNoUpdateDashboardResponse>(QUERY_QUALITY_DASHBOARD, {
        variables: queryInput ? { input: queryInput } : undefined,
        skip: !queryInput,
        fetchPolicy: 'cache-and-network',
    });

    if (error) {
        message.error(`查询失败：${error.message}`);
    }

    const result: NoUpdateDashboardResult | undefined = data?.queryQualityDashboard;
    const items: NoUpdateDashboardItem[] = useMemo(() => result?.items ?? [], [result]);
    const aggregate: NoUpdateDashboardAggregateItem[] = useMemo(() => result?.aggregate ?? [], [result]);

    const queryType = (timeRange?.queryType as ScaleQueryType | undefined) ?? ScaleQueryType.DAILY;

    /** 单期场景判定（DAILY 1 天 / WEEKLY 同 ISO 周 / MONTHLY 同月） */
    const isSinglePeriod = useMemo(() => {
        if (!timeRange) return false;
        const start = dayjs(timeRange.startDate);
        const end = dayjs(timeRange.endDate);
        if (!start.isValid() || !end.isValid()) return false;
        switch (timeRange.queryType) {
            case ScaleQueryType.DAILY:
                return start.isSame(end, 'day');
            case ScaleQueryType.WEEKLY:
                return start.isoWeek() === end.isoWeek() && start.isoWeekYear() === end.isoWeekYear();
            case ScaleQueryType.MONTHLY:
                return start.isSame(end, 'month');
            default:
                return false;
        }
    }, [timeRange]);

    /**
     * data2 拉取「上一周期」数据，用于：
     * - 单期：avgLabel 列展示上一周期值；环比对比基线
     * - DAILY 多期：周环比对比基线（拉「最后一天 T-7」那一天，对比时优先用表内 T-7 列，缺则用此结果）
     * WEEKLY/MONTHLY 多期不发请求（环比直接用相邻列）。
     */
    const prevPeriodInput: NoUpdateDashboardQueryInput | null = useMemo(() => {
        if (!queryInput || !timeRange) return null;
        const start = dayjs(timeRange.startDate);
        const end = dayjs(timeRange.endDate);
        if (!start.isValid() || !end.isValid()) return null;
        let prevStart: dayjs.Dayjs;
        let prevEnd: dayjs.Dayjs;
        switch (timeRange.queryType) {
            case ScaleQueryType.DAILY:
                // 单期 / 多期都拉「end 的 T-7」单日；多期场景作周环比基线
                prevStart = end.subtract(7, 'day');
                prevEnd = prevStart;
                break;
            case ScaleQueryType.WEEKLY:
                if (!isSinglePeriod) return null;
                prevStart = start.subtract(7, 'day');
                prevEnd = end.subtract(7, 'day');
                break;
            case ScaleQueryType.MONTHLY:
                if (!isSinglePeriod) return null;
                prevStart = start.subtract(1, 'month').startOf('month');
                prevEnd = prevStart.endOf('month');
                break;
            default:
                return null;
        }
        return {
            ...queryInput,
            startDate: prevStart.format('YYYY-MM-DD'),
            endDate: prevEnd.format('YYYY-MM-DD'),
        };
    }, [queryInput, timeRange, isSinglePeriod]);

    const { data: prevPeriodData } = useQuery<QueryNoUpdateDashboardResponse>(QUERY_QUALITY_DASHBOARD, {
        variables: prevPeriodInput ? { input: prevPeriodInput } : undefined,
        skip: !prevPeriodInput,
        fetchPolicy: 'cache-and-network',
    });
    const prevAggregate: NoUpdateDashboardAggregateItem[] = useMemo(
        () => prevPeriodData?.queryQualityDashboard?.aggregate ?? [],
        [prevPeriodData]
    );

    /** avgLabel 列名（按截图：日 → 日均/上周同日，周 → 周均/上周，月 → 月均/上月） */
    const avgLabel = useMemo(() => {
        if (!timeRange) {
            return queryType === ScaleQueryType.DAILY ? '日均' : queryType === ScaleQueryType.WEEKLY ? '周均' : '月均';
        }
        return resolveDashboardComparisonAvgLabel(timeRange.queryType, timeRange.startDate, timeRange.endDate);
    }, [timeRange, queryType]);

    /** 时间周期列（按周期排序）；默认隐藏近 3 天（美西）— 与 main 分支 QualitySharedPage 一致 */
    const periods = useMemo(() => {
        return Array.from(new Set(items.map((i) => i.timePeriod)))
            .sort()
            .filter((p) => !shouldHidePeriodForNoUpdate(p, queryType));
    }, [items, queryType]);

    /** 「items 按 (hubId, timePeriod)」索引 */
    const itemMap = useMemo(() => {
        const m = new Map<string, NoUpdateDashboardItem>();
        items.forEach((i) => {
            m.set(`${i.hubId}-${i.timePeriod}`, i);
        });
        return m;
    }, [items]);

    /** aggregate 按 hubId 索引（GraphQL Long → string，set 时归一化为 number 与 selectedHubId 对齐） */
    const aggregateMap = useMemo(() => {
        const m = new Map<number, NoUpdateDashboardAggregateItem>();
        aggregate.forEach((a) => m.set(Number(a.hubId), a));
        return m;
    }, [aggregate]);

    /**
     * 用于 avgLabel 列（与 mom 单期分支基线）的 aggregate：
     * - 单期：data2 上一周期 aggregate
     * - 多期：主查询 aggregate（区间均值）；注意 DAILY 多期虽然也发了 data2，但只为环比 T-7 用，avg 列仍取主查询
     */
    const avgAggregateMap = useMemo(() => {
        const source = isSinglePeriod ? prevAggregate : aggregate;
        const m = new Map<number, NoUpdateDashboardAggregateItem>();
        source.forEach((a) => m.set(Number(a.hubId), a));
        return m;
    }, [isSinglePeriod, prevAggregate, aggregate]);

    /** DAILY 多期周环比用：data2 items（T-7 那一天）按 hubId 索引 */
    const prevItemMap = useMemo(() => {
        const m = new Map<number, NoUpdateDashboardItem>();
        const items2 = prevPeriodData?.queryQualityDashboard?.items ?? [];
        items2.forEach((i) => m.set(Number(i.hubId), i));
        return m;
    }, [prevPeriodData]);

    /**
     * 当前命中的 hub ids（如选择具体 hub 则只有该 hub；否则展示全部 hub）。
     * 顺序：全美第一 → HUB_DISPLAY_ORDER 白名单（按 displayLabel 命中）→ 剩余 hub 按总包裹量 DESC。
     * 过滤 sortingCode 以 'TEST' 开头的测试 HUB。
     */
    const visibleHubIds = useMemo(() => {
        if (selectedHubId !== 0) {
            return [selectedHubId];
        }
        const codeByHub = new Map<number, string>();
        const volumeByHub = new Map<number, number>();
        aggregate.forEach((a) => {
            const hid = Number(a.hubId);
            codeByHub.set(hid, a.sortingCode ?? '');
            volumeByHub.set(
                hid,
                (a.linehaulInboundParcelCount ?? 0) +
                    (a.inHubOperationParcelCount ?? 0) +
                    (a.inHubLinehaulParcelCount ?? 0) +
                    (a.lastMileParcelCount ?? 0)
            );
        });
        const ids = aggregate
            .map((a) => Number(a.hubId))
            .filter((id) => {
                if (id === 0) return true;
                const code = codeByHub.get(id) ?? '';
                if (code.toUpperCase().startsWith('TEST')) return false;
                if (EXCLUDED_HUB_LABELS.has(formatHubDisplayLabel(code))) return false;
                return true;
            });
        const orderIndex = (id: number): number => {
            const idx = HUB_DISPLAY_ORDER.indexOf(formatHubDisplayLabel(codeByHub.get(id) ?? ''));
            return idx === -1 ? Number.POSITIVE_INFINITY : idx;
        };
        return Array.from(new Set(ids)).sort((a, b) => {
            if (a === 0) return -1;
            if (b === 0) return 1;
            const ia = orderIndex(a);
            const ib = orderIndex(b);
            if (ia !== ib) return ia - ib;
            return (volumeByHub.get(b) ?? 0) - (volumeByHub.get(a) ?? 0);
        });
    }, [aggregate, selectedHubId]);

    const hubLabelById = useCallback(
        (hubId: number): string => {
            // GraphQL Long → JS 字符串，避免 hubId === 0 严格比较失败
            if (Number(hubId) === 0) return '全美';
            const agg = aggregateMap.get(hubId);
            // formatHubDisplayLabel：DB 里 'LA' → UI 'LAX'，其余原值
            const code = agg?.sortingCode;
            return code ? formatHubDisplayLabel(code) : `Hub ${hubId}`;
        },
        [aggregateMap]
    );

    /**
     * 通用「环比文字 + 是否标红」计算：
     * - 单期 = 当期 vs avgRate（avgRate 来自 data2 上期 aggregate）
     * - DAILY 多期 = 最后一列 vs T-7（优先表内 T-7 列，缺则用 dailyT7Rate=data2 T-7 单日；都缺则不展示环比，不退化相邻日）
     * - WEEKLY/MONTHLY 多期 = 最后一列 vs 前一列
     * 标红条件：周同比 ≥15pp 且两期分母均 > 1000
     */
    const computeMom = useCallback(
        (
            rates: Record<string, string | null | undefined>,
            denoms: Record<string, number>,
            avgRate: string | null | undefined,
            avgDenom: number,
            /** DAILY 多期专用：T-7（基于最后一期）的当前 hub+指标 率/分母；其它场景不传 */
            dailyT7Rate?: string | null | undefined,
            dailyT7Denom?: number,
        ): { momChange: string | null; momHighlight: boolean } => {
            if (periods.length === 0) return { momChange: null, momHighlight: false };
            const last = periods[periods.length - 1];
            let currRate: string | null | undefined;
            let prevRate: string | null | undefined;
            let currDenom = 0;
            let prevDenom = 0;
            if (isSinglePeriod) {
                currRate = rates[last];
                prevRate = avgRate;
                currDenom = denoms[last] ?? 0;
                prevDenom = avgDenom;
            } else if (queryType === ScaleQueryType.DAILY) {
                currRate = rates[last];
                currDenom = denoms[last] ?? 0;
                const t7 = dayjs(last).subtract(7, 'day').format('YYYY-MM-DD');
                if (periods.includes(t7)) {
                    prevRate = rates[t7];
                    prevDenom = denoms[t7] ?? 0;
                } else if (dailyT7Rate != null) {
                    prevRate = dailyT7Rate;
                    prevDenom = dailyT7Denom ?? 0;
                } else {
                    return { momChange: null, momHighlight: false };
                }
            } else if (periods.length >= 2) {
                const prev = periods[periods.length - 2];
                currRate = rates[last];
                prevRate = rates[prev];
                currDenom = denoms[last] ?? 0;
                prevDenom = denoms[prev] ?? 0;
            } else {
                return { momChange: null, momHighlight: false };
            }
            const c = parseRatePct(currRate);
            const p = parseRatePct(prevRate);
            const momChange = c != null && p != null
                ? `${c - p >= 0 ? '+' : ''}${(c - p).toFixed(2)}pp`
                : null;
            const momHighlight = shouldHighlightMom(currRate, prevRate, currDenom, prevDenom);
            return { momChange, momHighlight };
        },
        [periods, isSinglePeriod, queryType]
    );

    /** 构建一个 metric row：填充每个时间周期的率、分子件数、分母 */
    const buildMetricRow = useCallback(
        (hubId: number, metric: MetricNode): TableRow => {
            const rates: Record<string, string | null | undefined> = {};
            const nums: Record<string, number> = {};
            const denoms: Record<string, number> = {};
            const numFields = NUM_FIELDS_BY_KEY[metric.key];
            const denomFields = DENOM_FIELDS_BY_KEY[metric.key];
            periods.forEach((p) => {
                const it = itemMap.get(`${hubId}-${p}`);
                rates[p] = it?.[metric.key] as string | null | undefined;
                nums[p] = sumNumFields(it, numFields);
                denoms[p] = sumNumFields(it, denomFields);
            });
            const agg = avgAggregateMap.get(hubId);
            const avgRate = agg ? (agg as unknown as Record<string, string | null | undefined>)[metric.key] : undefined;
            const avgDenom = sumNumFields(agg, denomFields);
            // DAILY 多期：从 prevItemMap（T-7 那一天）取该 hub+指标率/分母，作为周环比基线
            let dailyT7Rate: string | null | undefined;
            let dailyT7Denom = 0;
            if (queryType === ScaleQueryType.DAILY && !isSinglePeriod) {
                const prevIt = prevItemMap.get(hubId);
                dailyT7Rate = prevIt ? (prevIt as unknown as Record<string, string | null | undefined>)[metric.key] : null;
                dailyT7Denom = sumNumFields(prevIt, denomFields);
            }
            const { momChange, momHighlight } = computeMom(rates, denoms, avgRate, avgDenom, dailyT7Rate, dailyT7Denom);
            return {
                key: `${hubId}-${metric.depth}-${metric.key}`,
                hubId,
                hubLabel: hubLabelById(hubId),
                label: metric.label,
                metricKey: metric.key,
                depth: metric.depth,
                drillType: metric.drillType,
                annotation: metric.annotation,
                tooltip: metric.tooltip,
                targetFieldName: metric.targetFieldName,
                rates,
                nums,
                denoms,
                avgRate,
                avgDenom,
                momChange,
                momHighlight,
            };
        },
        [periods, itemMap, avgAggregateMap, prevItemMap, hubLabelById, computeMom, isSinglePeriod, queryType]
    );

    /**
     * 动态拆分只有一个责任仓/责任 hub 时，不提供下钻。
     *
     * 拆分维度唯一 ⇒ 汇总等于明细，子行只是父行换个名字（如 EWR 下「仓内作业断更率」只拆出 EWR，
     * 逐列数值与父行完全一致），展开没有信息量还多占一行。
     * 仅用于动态拆分层；静态子指标即使只有一个也代表不同指标，不走这里。
     */
    const drillableBreakdown = useCallback(
        (rows: TableRow[]): TableRow[] => (rows.length > 1 ? rows : []),
        []
    );

    /** 构建「尾程 → 责任 whs」动态 L3 行；rate 为该 whs 自己的断更率（分母=子项 parcelCount），denoms 用子项 parcelCount 用于标红门槛 */
    const buildLastMileWhsBreakdownRow = useCallback(
        (hubId: number, responsibleWarehouseId: number, label: string): TableRow => {
            const rates: Record<string, string | null | undefined> = {};
            const nums: Record<string, number> = {};
            const denoms: Record<string, number> = {};
            periods.forEach((p) => {
                const it = itemMap.get(`${hubId}-${p}`);
                const bd = it?.lastMileByResponsibleWhs?.find((b) => b.responsibleWarehouseId === responsibleWarehouseId);
                rates[p] = bd?.rate;
                nums[p] = Number(bd?.count ?? 0);
                denoms[p] = Number(bd?.parcelCount ?? 0);
            });
            const agg = avgAggregateMap.get(hubId);
            const aggBd = agg?.lastMileByResponsibleWhs?.find((b) => b.responsibleWarehouseId === responsibleWarehouseId);
            const avgRate = aggBd?.rate;
            const avgDenom = Number(aggBd?.parcelCount ?? 0);
            let dailyT7Rate: string | null | undefined;
            let dailyT7Denom = 0;
            if (queryType === ScaleQueryType.DAILY && !isSinglePeriod) {
                const prevBd = prevItemMap.get(hubId)?.lastMileByResponsibleWhs?.find(
                    (b) => b.responsibleWarehouseId === responsibleWarehouseId
                );
                dailyT7Rate = prevBd?.rate;
                dailyT7Denom = Number(prevBd?.parcelCount ?? 0);
            }
            const { momChange, momHighlight } = computeMom(rates, denoms, avgRate, avgDenom, dailyT7Rate, dailyT7Denom);
            return {
                key: `${hubId}-2-lastMileWhs-${responsibleWarehouseId}`,
                hubId,
                hubLabel: hubLabelById(hubId),
                label,
                metricKey: 'lastMileRate',
                depth: 2,
                drillType: NoUpdateDashboardDrillTypeInput.LAST_MILE,
                responsibleWarehouseId,
                rates,
                nums,
                denoms,
                avgRate,
                avgDenom,
                momChange,
                momHighlight,
            };
        },
        [periods, itemMap, avgAggregateMap, prevItemMap, hubLabelById, computeMom, isSinglePeriod, queryType]
    );

    /** 构建「干线接收 → 责任 hub（上游发货 hub）」动态 L4 行；rate 为该上游 hub 自己的断更率（分母=子项 parcelCount），denoms 用子项 parcelCount 用于标红门槛 */
    const buildLinehaulInboundHubBreakdownRow = useCallback(
        (hubId: number, responsibleHubId: number, label: string): TableRow => {
            const rates: Record<string, string | null | undefined> = {};
            const nums: Record<string, number> = {};
            const denoms: Record<string, number> = {};
            periods.forEach((p) => {
                const it = itemMap.get(`${hubId}-${p}`);
                const bd = it?.linehaulInboundByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
                rates[p] = bd?.rate;
                nums[p] = Number(bd?.count ?? 0);
                denoms[p] = Number(bd?.parcelCount ?? 0);
            });
            const agg = avgAggregateMap.get(hubId);
            const aggBd = agg?.linehaulInboundByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
            const avgRate = aggBd?.rate;
            const avgDenom = Number(aggBd?.parcelCount ?? 0);
            let dailyT7Rate: string | null | undefined;
            let dailyT7Denom = 0;
            if (queryType === ScaleQueryType.DAILY && !isSinglePeriod) {
                const prevBd = prevItemMap.get(hubId)?.linehaulInboundByResponsibleHub?.find(
                    (b) => b.responsibleHubId === responsibleHubId
                );
                dailyT7Rate = prevBd?.rate;
                dailyT7Denom = Number(prevBd?.parcelCount ?? 0);
            }
            const { momChange, momHighlight } = computeMom(rates, denoms, avgRate, avgDenom, dailyT7Rate, dailyT7Denom);
            return {
                key: `${hubId}-3-linehaulInboundHub-${responsibleHubId}`,
                hubId,
                hubLabel: hubLabelById(hubId),
                label,
                metricKey: 'linehaulInboundRate',
                depth: 3,
                drillType: NoUpdateDashboardDrillTypeInput.LINEHAUL_INBOUND,
                responsibleHubId,
                rates,
                nums,
                denoms,
                avgRate,
                avgDenom,
                momChange,
                momHighlight,
            };
        },
        [periods, itemMap, avgAggregateMap, prevItemMap, hubLabelById, computeMom, isSinglePeriod, queryType]
    );

    /** 构建「仓内作业 → 责任 hub」动态 L4 行；rate 为该 hub 自己的断更率（分母=子项 parcelCount），denoms 用子项 parcelCount 用于标红门槛 */
    const buildInHubOperationHubBreakdownRow = useCallback(
        (hubId: number, responsibleHubId: number, label: string): TableRow => {
            const rates: Record<string, string | null | undefined> = {};
            const nums: Record<string, number> = {};
            const denoms: Record<string, number> = {};
            periods.forEach((p) => {
                const it = itemMap.get(`${hubId}-${p}`);
                const bd = it?.inHubOperationByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
                rates[p] = bd?.rate;
                nums[p] = Number(bd?.count ?? 0);
                denoms[p] = Number(bd?.parcelCount ?? 0);
            });
            const agg = avgAggregateMap.get(hubId);
            const aggBd = agg?.inHubOperationByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
            const avgRate = aggBd?.rate;
            const avgDenom = Number(aggBd?.parcelCount ?? 0);
            let dailyT7Rate: string | null | undefined;
            let dailyT7Denom = 0;
            if (queryType === ScaleQueryType.DAILY && !isSinglePeriod) {
                const prevBd = prevItemMap.get(hubId)?.inHubOperationByResponsibleHub?.find(
                    (b) => b.responsibleHubId === responsibleHubId
                );
                dailyT7Rate = prevBd?.rate;
                dailyT7Denom = Number(prevBd?.parcelCount ?? 0);
            }
            const { momChange, momHighlight } = computeMom(rates, denoms, avgRate, avgDenom, dailyT7Rate, dailyT7Denom);
            return {
                key: `${hubId}-3-inHubOperationHub-${responsibleHubId}`,
                hubId,
                hubLabel: hubLabelById(hubId),
                label,
                metricKey: 'inHubOperationRate',
                depth: 3,
                drillType: NoUpdateDashboardDrillTypeInput.IN_HUB_OPERATION,
                responsibleHubId,
                rates,
                nums,
                denoms,
                avgRate,
                avgDenom,
                momChange,
                momHighlight,
            };
        },
        [periods, itemMap, avgAggregateMap, prevItemMap, hubLabelById, computeMom, isSinglePeriod, queryType]
    );

    /** 构建「干线运输 → 责任 hub」动态 L3 行；rate 为该 hub 自己的断更率（分母=子项 parcelCount），denoms 用子项 parcelCount 用于标红门槛 */
    const buildInHubLinehaulHubBreakdownRow = useCallback(
        (hubId: number, responsibleHubId: number, label: string): TableRow => {
            const rates: Record<string, string | null | undefined> = {};
            const nums: Record<string, number> = {};
            const denoms: Record<string, number> = {};
            periods.forEach((p) => {
                const it = itemMap.get(`${hubId}-${p}`);
                const bd = it?.inHubLinehaulByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
                rates[p] = bd?.rate;
                nums[p] = Number(bd?.count ?? 0);
                denoms[p] = Number(bd?.parcelCount ?? 0);
            });
            const agg = avgAggregateMap.get(hubId);
            const aggBd = agg?.inHubLinehaulByResponsibleHub?.find((b) => b.responsibleHubId === responsibleHubId);
            const avgRate = aggBd?.rate;
            const avgDenom = Number(aggBd?.parcelCount ?? 0);
            let dailyT7Rate: string | null | undefined;
            let dailyT7Denom = 0;
            if (queryType === ScaleQueryType.DAILY && !isSinglePeriod) {
                const prevBd = prevItemMap.get(hubId)?.inHubLinehaulByResponsibleHub?.find(
                    (b) => b.responsibleHubId === responsibleHubId
                );
                dailyT7Rate = prevBd?.rate;
                dailyT7Denom = Number(prevBd?.parcelCount ?? 0);
            }
            const { momChange, momHighlight } = computeMom(rates, denoms, avgRate, avgDenom, dailyT7Rate, dailyT7Denom);
            return {
                key: `${hubId}-2-linehaulHub-${responsibleHubId}`,
                hubId,
                hubLabel: hubLabelById(hubId),
                label,
                metricKey: 'inHubLinehaulRate',
                depth: 2,
                drillType: NoUpdateDashboardDrillTypeInput.IN_HUB_LINEHAUL,
                responsibleHubId,
                rates,
                nums,
                denoms,
                avgRate,
                avgDenom,
                momChange,
                momHighlight,
            };
        },
        [periods, itemMap, avgAggregateMap, prevItemMap, hubLabelById, computeMom, isSinglePeriod, queryType]
    );

    const tableData: TableRow[] = useMemo(() => {
        return visibleHubIds.map((hubId) => {
            const l1Row = buildMetricRow(hubId, { ...L1_METRIC });
            l1Row.label = hubLabelById(hubId);

            const hubLabel = formatHubDisplayLabel(aggregateMap.get(hubId)?.sortingCode ?? '');
            const isAllUs = Number(hubId) === 0;
            const metricTreeForHub = METRIC_TREE.filter((m) => {
                if (m.key === 'lastMileRate' && HUBS_HIDE_LAST_MILE.has(hubLabel)) return false;
                // 全美汇总不展示「干线运输断更率」（口径汇总意义不大，按 hub 看才合理）
                if (m.key === 'inHubLinehaulRate' && isAllUs) return false;
                return true;
            });

            const children = metricTreeForHub.map((l2) => {
                const l2Row = buildMetricRow(hubId, l2);
                const staticL3s = l2.children?.map((l3) => {
                    const l3Row = buildMetricRow(hubId, l3);
                    // 「干线接收断更率」L3 下挂动态 L4：按责任 hub（上游发货 hub）拆分
                    if (l3.key === 'linehaulInboundRate') {
                        const rawCodeById = new Map<number, string>();
                        periods.forEach((p) => {
                            itemMap.get(`${hubId}-${p}`)?.linehaulInboundByResponsibleHub?.forEach((b) => {
                                if (!rawCodeById.has(b.responsibleHubId)) {
                                    rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                                }
                            });
                        });
                        aggregateMap.get(hubId)?.linehaulInboundByResponsibleHub?.forEach((b) => {
                            if (!rawCodeById.has(b.responsibleHubId)) {
                                rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                            }
                        });
                        const dynamicL4s = Array.from(rawCodeById.entries())
                            .filter(([, raw]) => !raw.toUpperCase().startsWith('TEST'))
                            .map(([respHubId, raw]) => [respHubId, formatHubDisplayLabel(raw)] as [number, string])
                            .filter(([, label]) => !EXCLUDED_HUB_LABELS.has(label))
                            .sort(([, la], [, lb]) => compareHubDisplayLabel(la, lb))
                            .map(([respHubId, label]) => buildLinehaulInboundHubBreakdownRow(hubId, respHubId, label));
                        const drillableL4s = drillableBreakdown(dynamicL4s);
                        if (drillableL4s.length > 0) l3Row.children = drillableL4s;
                    } else if (l3.key === 'inHubOperationRate') {
                        // 「仓内作业断更率」L3 下挂动态 L4：按责任 hub 拆分
                        const rawCodeById = new Map<number, string>();
                        periods.forEach((p) => {
                            itemMap.get(`${hubId}-${p}`)?.inHubOperationByResponsibleHub?.forEach((b) => {
                                if (!rawCodeById.has(b.responsibleHubId)) {
                                    rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                                }
                            });
                        });
                        aggregateMap.get(hubId)?.inHubOperationByResponsibleHub?.forEach((b) => {
                            if (!rawCodeById.has(b.responsibleHubId)) {
                                rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                            }
                        });
                        const dynamicL4s = Array.from(rawCodeById.entries())
                            .filter(([, raw]) => !raw.toUpperCase().startsWith('TEST'))
                            .map(([respHubId, raw]) => [respHubId, formatHubDisplayLabel(raw)] as [number, string])
                            .filter(([, label]) => !EXCLUDED_HUB_LABELS.has(label))
                            .sort(([, la], [, lb]) => compareHubDisplayLabel(la, lb))
                            .map(([respHubId, label]) => buildInHubOperationHubBreakdownRow(hubId, respHubId, label));
                        const drillableL4s = drillableBreakdown(dynamicL4s);
                        if (drillableL4s.length > 0) l3Row.children = drillableL4s;
                    }
                    return l3Row;
                }) ?? [];

                let dynamicL3s: TableRow[] = [];
                if (l2.key === 'lastMileRate') {
                    // 收集所有 period + aggregate 出现过的 responsibleWarehouseId，按 sortingCode 排序
                    const labelById = new Map<number, string>();
                    periods.forEach((p) => {
                        itemMap.get(`${hubId}-${p}`)?.lastMileByResponsibleWhs?.forEach((b) => {
                            if (!labelById.get(b.responsibleWarehouseId)) {
                                labelById.set(b.responsibleWarehouseId, b.sortingCode ?? `WHS-${b.responsibleWarehouseId}`);
                            }
                        });
                    });
                    aggregateMap.get(hubId)?.lastMileByResponsibleWhs?.forEach((b) => {
                        if (!labelById.get(b.responsibleWarehouseId)) {
                            labelById.set(b.responsibleWarehouseId, b.sortingCode ?? `WHS-${b.responsibleWarehouseId}`);
                        }
                    });
                    dynamicL3s = Array.from(labelById.entries())
                        .filter(([, label]) => !EXCLUDED_HUB_LABELS.has(formatHubDisplayLabel(label)))
                        .sort((a, b) => a[1].localeCompare(b[1]))
                        .map(([whsId, label]) => buildLastMileWhsBreakdownRow(hubId, whsId, label));
                } else if (l2.key === 'inHubLinehaulRate') {
                    const rawCodeById = new Map<number, string>();
                    periods.forEach((p) => {
                        itemMap.get(`${hubId}-${p}`)?.inHubLinehaulByResponsibleHub?.forEach((b) => {
                            if (!rawCodeById.has(b.responsibleHubId)) {
                                rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                            }
                        });
                    });
                    aggregateMap.get(hubId)?.inHubLinehaulByResponsibleHub?.forEach((b) => {
                        if (!rawCodeById.has(b.responsibleHubId)) {
                            rawCodeById.set(b.responsibleHubId, b.sortingCode ?? `Hub-${b.responsibleHubId}`);
                        }
                    });
                    dynamicL3s = Array.from(rawCodeById.entries())
                        .filter(([, raw]) => !raw.toUpperCase().startsWith('TEST'))
                        .map(([respHubId, raw]) => [respHubId, formatHubDisplayLabel(raw)] as [number, string])
                        .filter(([, label]) => !EXCLUDED_HUB_LABELS.has(label))
                        .sort(([, la], [, lb]) => compareHubDisplayLabel(la, lb))
                        .map(([respHubId, label]) => buildInHubLinehaulHubBreakdownRow(hubId, respHubId, label));
                }

                const allChildren = [...staticL3s, ...drillableBreakdown(dynamicL3s)];
                if (allChildren.length > 0) l2Row.children = allChildren;
                return l2Row;
            });
            l1Row.children = children;
            return l1Row;
        });
    }, [
        visibleHubIds, buildMetricRow, hubLabelById, periods, itemMap, aggregateMap,
        buildLastMileWhsBreakdownRow, buildInHubLinehaulHubBreakdownRow, buildLinehaulInboundHubBreakdownRow,
        buildInHubOperationHubBreakdownRow, drillableBreakdown,
    ]);

    // 数据加载/刷新后默认全部折叠：只展示 L1 hub 行（全美 / LAX / EWR / ...）
    useEffect(() => {
        setExpandedKeys(new Set());
    }, [tableData]);

    const renderRateCell = (row: TableRow, period: string) => {
        return formatRatePct(row.rates[period]);
    };

    const handleCellClick = useCallback(
        (row: TableRow, period: string) => {
            if (!row.drillType) return;
            // 用真实分子件数判断（0.00% 但分子>0 也算有数据）
            if (!row.nums[period] || row.nums[period] <= 0) return;
            if (!isPeriodWithin30Days(period, queryType)) return;
            const { start, end } = periodToDateRange(period, queryType);
            if (!start || !end) return;
            setDrawerDrill({
                drillType: row.drillType,
                hubId: row.hubId,
                startDate: start,
                endDate: end,
                metricLabel: row.label,
                responsibleHubId: row.responsibleHubId,
                responsibleWarehouseId: row.responsibleWarehouseId,
            });
            setDrawerOpen(true);
        },
        [queryType]
    );

    /** 把树状 tableData 展平成导出行（按 depth 缩进 label，仅率值与表内显示一致） */
    const handleDownload = useCallback(async () => {
        if (!timeRange) {
            message.warning('请先选择时间范围');
            return;
        }
        if (tableData.length === 0 || periods.length === 0) {
            message.warning('当前条件下无断更数据可导出');
            return;
        }
        if (exportLoading) {
            message.warning('导出任务生成中，请稍候');
            return;
        }

        const momHeader = queryType === ScaleQueryType.DAILY ? '周环比' : '环比';
        const headers = ['指标', ...periods, avgLabel, momHeader];

        const flatten = (rows: TableRow[]): TableRow[] => {
            const out: TableRow[] = [];
            const walk = (r: TableRow) => {
                out.push(r);
                r.children?.forEach(walk);
            };
            rows.forEach(walk);
            return out;
        };
        const mainRows: string[][] = flatten(tableData).map((row) => {
            const indent = '  '.repeat(row.depth);
            const labelText = row.annotation ? `${indent}${row.label} ${row.annotation}` : `${indent}${row.label}`;
            return headers.map((h) => {
                if (h === '指标') return dashboardTableExportCellString(labelText);
                if (h === avgLabel) return dashboardTableExportCellString(formatRatePct(row.avgRate));
                if (h === momHeader) return dashboardTableExportCellString(row.momChange ?? '-');
                return dashboardTableExportCellString(formatRatePct(row.rates[h]));
            });
        });

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `qualityNoUpdate queryType=${timeRange.queryType} ${timeRange.startDate}..${timeRange.endDate} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: 'NoUpdate_Dashboard',
                            timezone: userTimezone,
                            snapshotSheetName: '断更数据',
                            mainSheetName: '断更数据',
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                            warehouseId: selectedHubId !== 0 ? selectedHubId : null,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败，请稍后重试');
                }
            } catch (e: unknown) {
                showError(e);
            }
        });
    }, [
        timeRange, tableData, periods, avgLabel, queryType, selectedHubId,
        exportLoading, runExportWithCooldown, generateDashboardTableExport,
    ]);

    const annotationDateKeys = useMemo(() => {
        // AVG 列固定用 range；WOW 列仅 DAILY 视图锚定单日 endDate，WEEKLY/MONTHLY 环比是两段 range 对比，range 才是正确签名
        const keys = new Set<string>([`${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`]);
        if (queryType === ScaleQueryType.DAILY) keys.add(timeRange?.endDate ?? '');
        periods.forEach((period) => keys.add(computeNoUpdateAnnotationDateKey(period, queryType)));
        return Array.from(keys);
    }, [periods, queryType, timeRange]);
    const {
        annotationsByKey,
        createAnnotation: createDashboardAnnotation,
        updateAnnotation: updateDashboardAnnotation,
        deleteAnnotation: deleteDashboardAnnotation,
        refetchAnnotations: refetchNoUpdateAnnotations,
    } = useDashboardAnnotations({ pageKey: PAGE_KEY_NO_UPDATE, dateKeys: annotationDateKeys });

    const markCtx = useDashboardMarkRules(
        DASHBOARD_MARK_CONFIG_KEY.NO_UPDATE,
        queryType === ScaleQueryType.DAILY
    );

    /**
     * 计算单元格标记。上周同日直接从本行已加载的 rates 取——默认 last15days(T-15~T-1)
     * 已覆盖 T-12 / T-11，无需补充请求；自定义短范围取不到时不标红框。
     */
    const resolveMarkForCell = useCallback(
        (row: TableRow, period: string): CellMark => {
            const ruleField = NO_UPDATE_MARK_RULE_FIELD_BY_METRIC[row.metricKey];
            if (!ruleField) return NO_CELL_MARK;
            const lastWeekDate = markCtx.lastWeekDateByField.get(ruleField);
            const isNation = Number(row.hubId) === 0;
            return resolveCellMark({
                ctx: markCtx,
                fieldName: ruleField,
                columnDate: period,
                row: {
                    isNation,
                    hubLabel: isNation ? undefined : row.hubLabel,
                    isDrillDown: row.depth >= 2,
                },
                cellValue: row.rates[period],
                lastWeekValue: lastWeekDate ? row.rates[lastWeekDate] : undefined,
            });
        },
        [markCtx]
    );

    const columns: ColumnsType<TableRow> = useMemo(() => {
        const wrapWithAnnotation = (
            columnType: DashboardAnnotationColumnType,
            dateKey: string,
            row: TableRow,
            node: React.ReactNode
        ): React.ReactNode => {
            // 动态拆分子行（按责任 hub/whs 展开）与父行共享同一个 metricKey 常量，
            // 仅这类行改用 row.key 做签名以区分彼此和父行；其余行沿用 metricKey，兼容历史注释数据
            const isDynamicBreakdownRow = row.responsibleHubId !== undefined || row.responsibleWarehouseId !== undefined;
            const annotationCellCtx: CellContext = {
                hubId: String(row.hubId),
                metricKey: isDynamicBreakdownRow ? row.key : row.metricKey,
                businessType: '',
                columnType,
                dateKey,
            };
            const annotationDims = noUpdateAnnotationConfig.buildCellDimensions(annotationCellCtx);
            const cellAnnotations = annotationsByKey.get(buildAnnotationKey(PAGE_KEY_NO_UPDATE, annotationDims, annotationCellCtx.dateKey, annotationCellCtx.columnType, annotationCellCtx.hubId ?? '', annotationCellCtx.metricKey));
            return (
                <MetricAnnotationCell
                    hubId={annotationCellCtx.hubId ?? ''}
                    metricKey={annotationCellCtx.metricKey}
                    dimensions={annotationDims}
                    dateKey={annotationCellCtx.dateKey}
                    columnType={annotationCellCtx.columnType}
                    annotations={cellAnnotations}
                    writeResource={noUpdateAnnotationConfig.writeResource}
                    adminResource={noUpdateAnnotationConfig.adminResource}
                    onCreate={createDashboardAnnotation}
                    onUpdate={updateDashboardAnnotation}
                    onDelete={deleteDashboardAnnotation}
                    onOpen={refetchNoUpdateAnnotations}
                >
                    {node}
                </MetricAnnotationCell>
            );
        };
        const baseColumns: ColumnsType<TableRow> = [
            {
                title: '指标',
                dataIndex: 'label',
                key: 'label',
                width: 320,
                fixed: 'left',
                render: (_: unknown, row: TableRow) => {
                    const showCollapse = (row.children?.length ?? 0) > 0;
                    const expanded = expandedKeys.has(row.key);
                    return (
                        <div className="flex items-center gap-2" style={{ paddingLeft: row.depth * 20 }}>
                            {showCollapse ? (
                                <span
                                    className="cursor-pointer text-blue-500 flex-shrink-0 inline-flex w-6 justify-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedKeys((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(row.key)) next.delete(row.key);
                                            else next.add(row.key);
                                            return next;
                                        });
                                    }}
                                >
                                    {expanded ? <DownOutlined /> : <RightOutlined />}
                                </span>
                            ) : row.depth > 0 ? (
                                <span className="inline-block w-6 flex-shrink-0" aria-hidden />
                            ) : null}
                            <span className={row.depth === 0 ? 'font-medium' : 'text-gray-600'}>
                                {row.label}
                                {row.tooltip ? <MetricTooltip content={appendTargetValueDesc(row.tooltip, markCtx, row.targetFieldName ?? row.metricKey, '\n')} /> : null}
                                {row.annotation ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                                        {row.annotation}
                                    </Typography.Text>
                                ) : null}
                            </span>
                        </div>
                    );
                },
            },
        ];

        periods.forEach((p) => {
            baseColumns.push({
                title: p,
                key: `period-${p}`,
                width: 110,
                align: 'right' as const,
                render: (_: unknown, row: TableRow) => {
                    const v = renderRateCell(row, p);
                    const dateKey = computeNoUpdateAnnotationDateKey(p, queryType);
                    const drillable = row.drillType && row.nums[p] > 0 && isPeriodWithin30Days(p, queryType);
                    const textClass = resolveMarkForCell(row, p).redText
                        ? 'dashboard-mark-text-danger'
                        : undefined;
                    if (drillable) {
                        return wrapWithAnnotation('RAW', dateKey, row, (
                            <span
                                className={textClass}
                                style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: '#722ED1',
                                    textUnderlineOffset: '2px',
                                }}
                                onClick={() => handleCellClick(row, p)}
                            >
                                {v}
                            </span>
                        ));
                    }
                    return wrapWithAnnotation('RAW', dateKey, row, (
                        <span className={textClass}>{v}</span>
                    ));
                },
                onCell: (row: TableRow) => ({
                    className: ['group/annot', markCellClassName(resolveMarkForCell(row, p))]
                        .filter(Boolean)
                        .join(' '),
                }),
            });
        });

        baseColumns.push({
            title: avgLabel,
            key: 'avgLabel',
            width: 110,
            align: 'right' as const,
            render: (_: unknown, row: TableRow) => {
                const dateKey = `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                return wrapWithAnnotation('AVG', dateKey, row, (
                    <span>
                        {formatRatePct(row.avgRate)}
                    </span>
                ));
            },
            onCell: () => ({ className: 'group/annot' }),
        });

        baseColumns.push({
            title: queryType === ScaleQueryType.DAILY ? '周环比' : '环比',
            key: 'momChange',
            width: 110,
            align: 'right' as const,
            render: (_: unknown, row: TableRow) => {
                const dateKey = queryType === ScaleQueryType.DAILY
                    ? (timeRange?.endDate ?? '')
                    : `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                if (row.momChange == null) return wrapWithAnnotation('WOW', dateKey, row, '-');
                return wrapWithAnnotation('WOW', dateKey, row, (
                    <span>
                        {row.momChange}
                    </span>
                ));
            },
            onCell: () => ({ className: 'group/annot' }),
        });

        return baseColumns;
    }, [
        periods, expandedKeys, avgLabel, handleCellClick, queryType, markCtx, resolveMarkForCell,
        timeRange, annotationsByKey, createDashboardAnnotation, updateDashboardAnnotation, deleteDashboardAnnotation,
    ]);

    /** AntD Table expandable 行控制：自定义 expandedRowKeys，避免内置 expandable column */
    const expandedRowKeys = useMemo(() => Array.from(expandedKeys), [expandedKeys]);

    return (
        <div className="p-6 min-h-full flex flex-col">
        <Card
            title={
                <span className="text-xl font-semibold">
                    断更数据看板
                    <span className="text-sm font-normal ml-3">（时区说明: 日期均为美西时区；明细下钻只支持最近30天{result?.latestCalculatedAt && `；数据更新于: ${formatCalculatedAt(result.latestCalculatedAt)}`}）</span>
                </span>
            }
            bordered={false}
            className="flex-1"
        >
            <div className="mb-4">
                <TimeRangeFilter
                    onChange={setTimeRange}
                    maxDaysBack={365}
                    dashboardTimezone={DASHBOARD_PACIFIC_TZ}
                    presetKeysByQueryType={{
                        [ScaleQueryType.DAILY]: ['last15days', 'custom'],
                    }}
                    defaultPresetByQueryType={{
                        [ScaleQueryType.DAILY]: 'last15days',
                    }}
                />
            </div>
            <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                    <Select
                        style={{ width: 320 }}
                        value={selectedHubId}
                        onChange={setSelectedHubId}
                        options={hubOptions}
                        loading={hubsLoading}
                        placeholder="选择分拣仓"
                    />
                </div>
                <Button
                        type="primary"
                        onClick={() => void handleDownload()}
                        loading={exportLoading}
                        disabled={exportLoading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{getExportButtonLabel()}</span>
                    </Button>
            </div>
            <DashboardMarkLegend ctx={markCtx} metricLabels={NO_UPDATE_MARK_METRIC_LABELS} />
            <Spin spinning={loading}>
                <Table<TableRow>
                    rowKey="key"
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    expandable={{
                        expandedRowKeys,
                        showExpandColumn: false,
                        childrenColumnName: 'children',
                    }}
                    scroll={{ x: 'max-content' }}
                    sticky
                    bordered
                    size="middle"
                />
            </Spin>
            <NoUpdateDetailDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                drillContext={drawerDrill}
                noUpdateStatus={noUpdateStatus}
                hubList={hubOptions
                    .filter((o) => Number(o.value) !== 0)
                    .map((o) => ({ id: Number(o.value), name: String(o.label) }))}
            />
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.quality}
            />
        </Card>
        </div>
    );
};

/** 时间周期字符串 → start/end 日期（YYYY-MM-DD） */
function periodToDateRange(period: string, queryType: ScaleQueryType): { start: string; end: string } | { start: ''; end: '' } {
    if (queryType === ScaleQueryType.DAILY && /^\d{4}-\d{2}-\d{2}$/.test(period)) {
        return { start: period, end: period };
    }
    if (queryType === ScaleQueryType.WEEKLY && /^\d{4}-\d{2}-\d{2}~\d{4}-\d{2}-\d{2}$/.test(period)) {
        const [s, e] = period.split('~');
        return { start: s, end: e };
    }
    if (queryType === ScaleQueryType.MONTHLY && /^\d{4}-\d{2}$/.test(period)) {
        const month = dayjs(`${period}-01`);
        return { start: month.startOf('month').format('YYYY-MM-DD'), end: month.endOf('month').format('YYYY-MM-DD') };
    }
    return { start: '', end: '' };
}

function formatCalculatedAt(iso: string): string {
    try {
        return dayjs(iso).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm');
    } catch {
        return iso;
    }
}

export default NoUpdateDashboardPage;
